/**
 * Welche Rechnung eine Buchung festschreibt — und welche nur benannt wird.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  DIE REGEL, UND WARUM SIE NICHT AM STATUS HÄNGT
 * ════════════════════════════════════════════════════════════════════════════
 *
 * „Abgereister Gast gehört nach der Abrechnung unveränderbar." Nicht der Status
 * `checked_out` sperrt also, sondern die ausgestellte Rechnung. Das ist zugleich
 * genauer und großzügiger als der Status:
 *
 * - Ein Gast, der heute früh abgereist ist und dessen Rechnung noch nicht
 *   geschrieben wurde, ist ein völlig normaler Korrekturfall — er ist einen Tag
 *   früher gefahren, der Vermieter zieht die Buchung abends zurecht.
 * - Eine **bestätigte** Buchung in vier Wochen, für die schon eine
 *   Anzahlungsrechnung ausgestellt wurde, hätte der Status durchgelassen — und
 *   genau dort entsteht der Fehler, den niemand bemerkt: die Buchung sagt vier
 *   Nächte, die ausgestellte Rechnung drei. Das kommt beim Jahresabschluss
 *   wieder hoch oder beim Gast, und dann ist es teuer.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  WARUM DIESE FUNKTIONEN REIN SIND
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Weil sie Geld betreffen und dieses Projekt keine DOM-Testumgebung hat. Eine
 * Regel, die in einer React-Komponente wohnt, ist hier dauerhaft nur von Hand
 * prüfbar — und sie steht dann leicht dreimal leicht verschieden da. Hier steht
 * sie einmal; Store und Ansichten fragen nach.
 *
 * Gesperrt sind damit Anreise, Abreise und Einheit. **Nicht** Status,
 * Zahlungen, Notizen oder Gastzuordnung: Einchecken, Zahlungseingang und
 * Anonymisierung müssen auch an einer abgerechneten Buchung weiter möglich
 * sein.
 */
import type { Invoice } from '../data/model'

/**
 * Die Rechnungen, die den Zeitraum dieser Buchung festschreiben.
 *
 * Leer heißt: änderbar. Die Grenze, Feld für Feld:
 *
 * - **ausgestellt** (`issued_at`) ist die Grenze, die die Datenbank ohnehin
 *   zieht: erst beim Ausstellen wird die Nummer vergeben und die Zeile
 *   unveränderlich. Ein Entwurf hat keine Nummer, hat das Haus nicht verlassen
 *   und ist zum Umschreiben da.
 * - **Anzahlungsrechnungen zählen mit.** Sie tragen eine Nummer und nennen den
 *   Zeitraum — das ist der Fall, den eine Sperre über den Status übersehen
 *   hätte.
 * - **Proformarechnungen zählen nicht.** Eine Proforma ist eine
 *   Zahlungsaufforderung, kein Belegdokument; `lib/guests.ts` zieht dieselbe
 *   Grenze bereits genauso. Zwei verschiedene Bedeutungen von „ausgestellt" in
 *   einem Produkt wären der eigentliche Fehler.
 * - **Gutschriften und stornierte Rechnungen zählen nicht.** Nach einer
 *   Gutschrift ist eine neue Rechnung der normale Weg, und eine Sperre davor
 *   wäre eine Sperre vor dem Richtigen. Das ist zugleich der Ausweg: Rechnung
 *   stornieren, Daten ändern, neu ausstellen.
 *
 * Sortiert nach Ausstellung, die jüngste zuerst — das Blatt nennt sie zuerst.
 */
export function lockingInvoices(invoices: Invoice[], bookingId: string): Invoice[] {
  return invoices
    .filter((i) =>
      i.booking_id === bookingId &&
      Boolean(i.issued_at) &&
      i.type !== 'proforma' && i.type !== 'credit_note' &&
      i.status !== 'cancelled')
    .sort((a, b) => (b.issued_at ?? '').localeCompare(a.issued_at ?? ''))
}

/**
 * Die Rechnungsentwürfe zu dieser Buchung. Sie sperren nicht.
 *
 * Sie werden benannt und stehen gelassen: ein Entwurf enthält nach einer
 * Änderung noch die alten Nächte, und der Vermieter muss das wissen. Er wird
 * aber NICHT heimlich mitgeschrieben — ein Dokument, das sich unter der Hand
 * ändert, während es womöglich gerade in einem zweiten Tab offen ist, ist
 * schlimmer als ein veraltetes.
 *
 * Ein Gutschrift-Entwurf gehört nicht dazu: „enthält noch die alten Nächte" ist
 * über eine Gutschrift keine wahre Aussage. Ein stornierter Entwurf auch nicht —
 * dieselbe Regel, die im Buchungsblatt schon für die Rechnungsliste gilt.
 */
export function draftInvoices(invoices: Invoice[], bookingId: string): Invoice[] {
  return invoices
    .filter((i) =>
      i.booking_id === bookingId &&
      !i.issued_at &&
      i.type !== 'credit_note' &&
      i.status !== 'cancelled')
    .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))
}
